import Logger from "../../clients/sentry";
import {
  designStageCaches,
  fetchDesignStages,
  fetchEstimateExport,
  fetchMFCodes,
  fetchUFCodes,
  fetchUseGroups,
  fetchUsersByIds,
  fetchVMCategoriesByProjectId,
  fetchEstimateExportUncached,
  fetchEstimateMilestone,
  milestoneCaches,
  stageEstimateExport,
  Result,
  sortCaches,
  userCaches,
  vmCategoryCaches,
  watchEstimateExportForClear,
} from "../../api";
import { CacheHandle, clearTogether } from "../../api/helpers";
import { fetchUnitSpecAssociations, fetchUnitSpecs, unitCaches } from "../../api/units";
import { ValidationError, serializeErrorForWarning } from "../../errors";
import { getAlternateCreatorIds } from "./alternates";
import { Alternate } from "../../interfaces/Alternate";
import { FloorGroup } from "../../interfaces/AreaSheet";
import { Estimate } from "../../interfaces/Estimate";
import { EstimateData, EstimateSystem } from "../../interfaces/EstimateData";
import { EstimateUseGroup } from "../../interfaces/EstimateUseGroup";
import { LineItem, LineItemData } from "../../interfaces/LineItem";
import { Markup, MarkupData } from "../../interfaces/Markup";
import { OverlineQuantitiesMap } from "../../interfaces/OverlineTotals";
import { Room } from "../../interfaces/Room";
import { SortCodeItem } from "../../interfaces/SortCodeMap";
import { SortField } from "../../interfaces/SortField";
import { defaultLineItem } from "../utils/lineItems";
import { omitKeys } from "../utils/omitKeys";
import { createOverlineQuantitiesMap } from "./overlineTotals";
import { MFUFlookupByDepth } from "../utils/sortCodes";
import {
  SortCodeLabelJunctionMap,
  buildLabelSortFieldColumnNameMap,
  buildSortCodeLabelJunctionMap,
  buildSortFieldColumnNameMap,
  createSortCodeLookup,
  getSortFieldsWithCodes,
  unpackAllSortCodes,
  unpackLabelColumns,
} from "./sortCodes";
import { blendHexWithWhite } from "../styles/colors";
import { assignLaborCrewFields } from "./laborCrews";
import { parseLineItem } from "./lineItems";
import { calculateAllocatedMarkups, parseMarkup } from "./markups";
import { excludedKeys as lineItemExcludedKeys } from "../../interfaces/LineItem";
import { excludedKeys as markupExcludedKeys } from "../../interfaces/Markup";

export interface ProcessedEstimate {
  baseBidItems: Array<LineItem>;
  alternateItems: Array<LineItem>;
  childItems: Array<LineItem>;
  baseBidRowFills: (string | null)[];
  alternateRowFills: (string | null)[];
  childRowFills: (string | null)[];
  estimateData: EstimateData;
  usedSortFields: { [key: string]: SortField };
  sortFieldColumnNameMap: { [key: string]: string };
  sortCodeLabelJunctionMap: SortCodeLabelJunctionMap;
  mfLookup: MFUFlookupByDepth;
  ufLookup: MFUFlookupByDepth;
  labelSortFieldColumnNameMap: Record<string, string>;
}

let processedEstimateCache: Map<string, Promise<ProcessedEstimate | undefined>> = new Map();

/** Every lookup the estimate owns, for clearing them as one. */
export const estimateLookupCaches: CacheHandle[] = [
  ...milestoneCaches,
  ...sortCaches,
  ...designStageCaches,
  ...vmCategoryCaches,
  ...userCaches,
  ...unitCaches,
];

export const clearProcessedEstimateCache = () => {
  processedEstimateCache = new Map();
};

const clearEstimateLookupCaches = (): void => clearTogether(estimateLookupCaches);

const loadEstimateLookups = async (projectId: string, estimateData: EstimateData): Promise<void> => {
  const estimateId = estimateData.estimate.id;
  const creatorIds = getAlternateCreatorIds(estimateData.alternates ?? []);
  const milestoneId = estimateData.estimate.milestone;
  const loads: Promise<unknown>[] = [
    milestoneId ? fetchEstimateMilestone(projectId, milestoneId) : Promise.resolve(),
    getProcessedEstimate(estimateId),
    fetchDesignStages(),
    fetchUseGroups(),
    fetchVMCategoriesByProjectId(projectId),
    creatorIds.length > 0 ? fetchUsersByIds(creatorIds) : Promise.resolve(),
    fetchUnitSpecs(),
    fetchUnitSpecAssociations(),
  ];
  await Promise.all(loads.map((load) => load.catch(() => undefined)));
};

/**
 * Fetch the estimate's export fresh and swap it into the cache, then reload its lookups, its
 * milestone among them. A failed export leaves the caches as they were; a failed lookup doesn't
 * fail the refresh. With `awaitLookups` false it returns once the payload is cached, and the
 * lookups finish in the background; reads of them wait on the requests already running.
 */
export const refreshEstimateDatasource = async (
  projectId: string,
  estimateId: string,
  { awaitLookups = true }: { awaitLookups?: boolean } = {}
): Promise<Result<EstimateData>> => {
  const notCleared = watchEstimateExportForClear();
  const result = await fetchEstimateExportUncached(estimateId);
  if (!result.success || !result.data) return result;
  const staged = stageEstimateExport(estimateId, result.data, notCleared);
  // A clear while this ran belongs to a newer selection, whose lookups this must not drop.
  if (notCleared()) {
    clearProcessedEstimateCache();
    clearEstimateLookupCaches();
    const lookups = loadEstimateLookups(projectId, result.data);
    if (awaitLookups) await lookups;
  }
  return staged;
};

export const getOverlineQuantities = (processed: ProcessedEstimate): Promise<OverlineQuantitiesMap> =>
  createOverlineQuantitiesMap(
    processed.estimateData.estimate.id,
    processed.usedSortFields,
    processed.estimateData.alternates || [],
    processed.mfLookup,
    processed.ufLookup,
    processed.sortFieldColumnNameMap,
    processed.labelSortFieldColumnNameMap
  );

export const getProcessedEstimate = async (estimateId: string): Promise<ProcessedEstimate | undefined> => {
  if (processedEstimateCache.has(estimateId)) {
    return processedEstimateCache.get(estimateId)!;
  }
  const promise = getProcessedEstimateUncached(estimateId);
  processedEstimateCache.set(estimateId, promise);
  // Only its own entry: a refresh may have replaced the map and started a newer build meanwhile.
  promise.catch(() => {
    if (processedEstimateCache.get(estimateId) === promise) processedEstimateCache.delete(estimateId);
  });
  return promise;
};

const getProcessedEstimateUncached = async (estimateId: string): Promise<ProcessedEstimate | undefined> => {
  const [estimateDataResult, ufCodeResult, mfCodeResult] = await Promise.all([
    fetchEstimateExport(estimateId),
    fetchUFCodes(),
    fetchMFCodes(),
  ]);

  if (!estimateDataResult.success) {
    throw estimateDataResult.error;
  }
  if (!ufCodeResult.success) {
    throw ufCodeResult.error;
  }
  if (!mfCodeResult.success) {
    throw mfCodeResult.error;
  }

  const estimateData = estimateDataResult.data;
  const mfCodes: SortCodeItem[] = mfCodeResult.data || [];
  const ufCodes: SortCodeItem[] = ufCodeResult.data || [];
  const mfLookup = createSortCodeLookup(mfCodes);
  const ufLookup = createSortCodeLookup(ufCodes);

  if (!estimateData) {
    throw new ValidationError("Estimate failed to load");
  }
  if (!estimateData.line_items || estimateData.line_items.length === 0) {
    console.error("Estimate has no line items");
  }

  const lineItems: LineItem[] = estimateData.line_items;
  const markups: Markup[] = estimateData.estimate_markups || [];

  const uniqueSortFieldIds = new Set<string>();
  lineItems.forEach((item) => {
    Object.keys(item.extras).forEach((id) => uniqueSortFieldIds.add(id));
  });
  markups.forEach((markup) => {
    Object.keys(markup.extras ?? {}).forEach((id) => uniqueSortFieldIds.add(id));
  });

  const usedSortFields: { [key: string]: SortField } = await getSortFieldsWithCodes(
    Array.from(uniqueSortFieldIds),
    estimateData.projectId
  );
  const sortFieldColumnNameMap = buildSortFieldColumnNameMap(usedSortFields);

  // Build SCLO junction map and label column name map
  const { junctionMap: sortCodeLabelJunctionMap, labelBearingSortFieldIds } = buildSortCodeLabelJunctionMap(
    estimateData.line_item_sort_code_labels || []
  );
  const labelSortFieldColumnNameMap = buildLabelSortFieldColumnNameMap(
    sortFieldColumnNameMap,
    labelBearingSortFieldIds
  );

  addRoomsToStats(estimateData);

  const [baseBidItems, alternateItems, childItems, baseBidRowFills, alternateRowFills, childRowFills] =
    buildEstimateRecords(
      estimateData,
      usedSortFields,
      ufLookup,
      mfLookup,
      sortFieldColumnNameMap,
      sortCodeLabelJunctionMap,
      labelSortFieldColumnNameMap
    );
  return {
    baseBidItems,
    alternateItems,
    childItems,
    baseBidRowFills,
    alternateRowFills,
    childRowFills,
    estimateData,
    usedSortFields,
    sortFieldColumnNameMap,
    sortCodeLabelJunctionMap,
    labelSortFieldColumnNameMap,
    mfLookup,
    ufLookup,
  };
};

const buildEstimateRecords = (
  data: EstimateData,
  usedSortFields: { [key: string]: SortField },
  ufLookup: MFUFlookupByDepth,
  mfLookup: MFUFlookupByDepth,
  sortFieldColumnNameMap: Record<string, string>,
  sortCodeLabelJunctionMap: SortCodeLabelJunctionMap = new Map(),
  labelSortFieldColumnNameMap: Record<string, string> = {}
): [Array<LineItem>, Array<LineItem>, Array<LineItem>, (string | null)[], (string | null)[], (string | null)[]] => {
  let baseBidRecords: Array<LineItem> = [];
  let alternateRecords: Array<LineItem> = [];
  let childRecords: Array<LineItem> = [];
  const baseBidRowFills: (string | null)[] = [];
  const alternateRowFills: (string | null)[] = [];
  const childRowFills: (string | null)[] = [];
  const estimate: Estimate = data.estimate || {};
  const alternates: Array<Alternate> = data.alternates || [];
  const systems: Array<EstimateSystem> = data.estimate_systems || [];
  const useGroupsArray: Array<EstimateUseGroup> = (estimate.stats && estimate.stats.useGroups) || [];
  const floorGroups: Array<FloorGroup> = data.floor_groups || [];

  try {
    const useGroups: { [key: string]: EstimateUseGroup } = useGroupsArray.reduce(
      (acc: { [key: string]: EstimateUseGroup }, useGroup: EstimateUseGroup) => {
        acc[useGroup.id] = useGroup;
        return acc;
      },
      {}
    );

    const rawLineItems: LineItemData[] = data.line_items || [];

    const parentLineItemLookup: Record<string, LineItemData> = rawLineItems.reduce(
      (acc: Record<string, LineItemData>, item: LineItemData) => {
        acc[item.id] = item;
        return acc;
      },
      {} as Record<string, LineItemData>
    );

    const lineItems: Array<LineItem> = rawLineItems.map((item: LineItemData) => {
      if (item.parent) {
        // child items need to inherit the alternate & system from their parent
        const parent = parentLineItemLookup[item.parent];
        if (parent) {
          item.estimate_system = parent.estimate_system;
          item.alternate = parent.alternate;
          item.estimate_use_groups = parent.estimate_use_groups;
        }
      }
      return parseLineItem(item, alternates, useGroups, systems, floorGroups);
    });
    assignLaborCrewFields(lineItems, data);

    const markups: Array<Markup> = (data.estimate_markups || []).map((item: MarkupData) =>
      parseMarkup(item, data.estimate_markups, alternates, estimate.stats, useGroups, floorGroups)
    );

    // Calculate allocated markups for line items
    calculateAllocatedMarkups(lineItems, markups, alternates);

    // Markups with no use-group allocation are prorated by each use group's share of the base-bid
    // line-item subtotal, as the web app's Schedule of Values does. Summed here from the live
    // line items rather than read from estimate.stats, which a worker refreshes after edits and
    // so can lag the markups the export serves alongside it.
    let baseBidSubtotal = 0;
    const baseBidUseGroupTotals: Record<string, number> = {};

    for (const item of lineItems) {
      const rec: LineItem = {
        ...item,
        type: "line_item",
      } as LineItem;
      if (item.parent) {
        rec.parent = item.parent;
        rec.parent_name = parentLineItemLookup[item.parent || ""]?.name;
      }
      rec.total = item.total_cost_text ?? (Number(item.total) || 0);
      const itemTotal = rec.total_cost_text ? 0 : Number(rec.total) || 0;
      const isBaseBidRow = Boolean(item.baseBid) && !item.parent;
      if (isBaseBidRow) baseBidSubtotal += itemTotal;

      for (const ug of useGroupsArray) {
        const label = ug.label || ug.use_group;
        const alloc =
          (item.estimate_use_groups && ug.id in item.estimate_use_groups ? item.estimate_use_groups[ug.id] : 0) / 100;
        const useGroupTotal = itemTotal * alloc;
        rec[`${label}_$`] = useGroupTotal;
        if (isBaseBidRow) baseBidUseGroupTotals[ug.id] = (baseBidUseGroupTotals[ug.id] ?? 0) + useGroupTotal;
      }

      for (const fg of floorGroups) {
        const label = fg.label;
        const myUseGroups = useGroupsArray.filter((ug) => ug.floor_group === fg.id);
        let t = 0;
        for (const mug of myUseGroups) {
          t =
            t + (item.estimate_use_groups && mug.id in item.estimate_use_groups ? item.estimate_use_groups[mug.id] : 0);
        }
        const alloc = t / 100;
        rec[`${label}_$$`] = itemTotal * alloc;
      }

      // Unpack all sort codes (mf, uf, extras)
      unpackAllSortCodes(rec, mfLookup, ufLookup, usedSortFields, sortFieldColumnNameMap);
      unpackLabelColumns(rec, sortCodeLabelJunctionMap, labelSortFieldColumnNameMap, sortFieldColumnNameMap);

      const reducedItem = omitKeys(rec, Array.from(lineItemExcludedKeys) as readonly (keyof typeof rec)[]) as LineItem;
      const rawForColor = parentLineItemLookup[item.id];
      const rowFill = lineItemRowBackgroundFill(rawForColor);
      if (isBaseBidRow) {
        delete reducedItem.baseBid;
        delete reducedItem.parent;
        baseBidRecords.push(reducedItem);
        baseBidRowFills.push(rowFill);
      }
      // Membership is the alternate UUID, not the display string — parseLineItem renders
      // item.alternate as "" when the alternate's key is blank, which would silently drop
      // the item from alternate reports and understate VM Summary totals
      if (item.alternateId && !item.parent) {
        delete reducedItem.parent;
        alternateRecords.push(reducedItem);
        alternateRowFills.push(rowFill);
      }
      if (item.parent) {
        childRecords.push(reducedItem);
        childRowFills.push(rowFill);
      }
    }

    for (const m of markups) {
      const isAboveTheLine = m.allocation != null;
      const markup_allocation = isAboveTheLine ? -m.total : 0;
      const total_plus_markup = (Number(m.total) || 0) + markup_allocation;
      const rec: LineItem = { ...defaultLineItem, ...m } as LineItem;
      rec.order = m.order.toString();
      rec.name = m.description || "";
      rec.quantity = m.percentage || 0;
      rec.uom = "%";
      rec.total = Number(m.total) || 0;
      rec.markup_allocation = markup_allocation;
      rec.total_plus_markup = total_plus_markup;
      rec.type = isAboveTheLine ? "atl_markup" : "markup";
      rec.alternateId = m.alternateId || "";
      rec.alternate = m.alternate || "";
      rec.alternate_desc = m.alternate_desc || "";
      rec.alternate_code = m.alternate_code || "";
      rec.alternateStatus = m.alternateStatus || "";
      rec.id = m.id;

      // Unpack all sort codes (mf, uf, extras)
      unpackAllSortCodes(rec, mfLookup, ufLookup, usedSortFields, sortFieldColumnNameMap);
      unpackLabelColumns(rec, sortCodeLabelJunctionMap, labelSortFieldColumnNameMap, sortFieldColumnNameMap);

      const total = rec.total as number;
      const hasUseGroupAllocation = Object.keys(m.estimate_use_groups).length > 0;
      for (const ug of useGroupsArray) {
        const label = ug.label || ug.use_group;
        if (hasUseGroupAllocation) {
          const alloc = (ug.id in m.estimate_use_groups ? m.estimate_use_groups[ug.id] : 0) / 100;
          rec[`${label}_$`] = total * alloc;
        } else {
          const share = baseBidSubtotal ? (baseBidUseGroupTotals[ug.id] ?? 0) / baseBidSubtotal : 0;
          rec[`${label}_$`] = total * share;
        }
      }

      for (const fg of floorGroups) {
        const label = fg.label;
        const myUseGroups = useGroupsArray.filter((ug) => ug.floor_group === fg.id);
        let t = 0;
        if (hasUseGroupAllocation) {
          for (const mug of myUseGroups) {
            t = t + (mug.id in m.estimate_use_groups ? m.estimate_use_groups[mug.id] : 0);
          }
          rec[`${label}_$$`] = total * (t / 100);
        } else {
          for (const mug of myUseGroups) {
            t = t + (baseBidUseGroupTotals[mug.id] ?? 0);
          }
          rec[`${label}_$$`] = baseBidSubtotal ? total * (t / baseBidSubtotal) : 0;
        }
      }

      const reducedMarkupItem = omitKeys(
        rec,
        Array.from(markupExcludedKeys) as readonly (keyof typeof rec)[]
      ) as LineItem;

      if (m.alternate) {
        alternateRecords.push(reducedMarkupItem);
        alternateRowFills.push(null);
      } else {
        baseBidRecords.push(reducedMarkupItem);
        baseBidRowFills.push(null);
      }
    }
  } catch (error) {
    Logger.warn("Error with ESTIMATE etl", serializeErrorForWarning(error));
  }

  return [baseBidRecords, alternateRecords, childRecords, baseBidRowFills, alternateRowFills, childRowFills];
};

const addRoomsToStats = (estimateData: EstimateData): void => {
  // Check if the necessary data exists before proceeding
  if (
    !estimateData ||
    !estimateData.rooms ||
    !estimateData.estimate ||
    !estimateData.estimate.stats ||
    !estimateData.estimate.stats.useGroups
  ) {
    console.warn("Invalid estimate data structure");
    return;
  }

  const roomStatsTotals: { [key: string]: { totalArea: number; totalRooms: number } } = {};

  // Accumulate total area and room count for each use group
  estimateData.rooms.forEach((room: Room) => {
    if (!room || !room.estimate_use_group || !room.area_average || !room.quantity) {
      console.warn("Invalid room data or missing use group reference for room:", room.id);
      return; // Skip invalid room entries
    }

    const useGroupId = room.estimate_use_group;
    const roomQuantity = Number(room.quantity.value);
    const roomArea = room.area_average.value * roomQuantity;

    // Ensure we can accumulate stats for this use group
    if (!roomStatsTotals[useGroupId]) {
      roomStatsTotals[useGroupId] = {
        totalArea: 0,
        totalRooms: 0,
      };
    }

    // Accumulate room area and room count for the corresponding use group
    roomStatsTotals[useGroupId].totalArea += roomArea;
    roomStatsTotals[useGroupId].totalRooms += roomQuantity;
  });

  // Update the stats for each use group, handling bad references
  estimateData.estimate.stats.useGroups?.forEach((useGroup: EstimateUseGroup) => {
    if (!useGroup || !useGroup.id) {
      console.warn("Invalid use group entry");
      return; // Skip invalid use group entries
    }

    const useGroupId = useGroup.id;
    if (roomStatsTotals[useGroupId] !== undefined) {
      useGroup.room_area = roomStatsTotals[useGroupId].totalArea;
      useGroup.room_count = roomStatsTotals[useGroupId].totalRooms;
    }
  });
};

function lineItemRowBackgroundFill(raw: LineItemData | undefined): string | null {
  const c = raw?.color;
  if (c == null || String(c).trim() === "") {
    return null;
  }
  return blendHexWithWhite(String(c).trim(), 0.2);
}
